import { useEffect, useState } from 'react';

import { api, ApiError } from '../api';
import { useLoad } from '../data';
import { fmtMoney } from '../ui';
import { Money } from '../types';

// E2 step 1 — the invitation entry screen, pre-session by definition:
// /#/join?t={token}. The ink hero leads with the inviting hotel and two real
// numbers from that hotel's own history — never a platform pitch. "Yes, set
// me up" and "Not now" carry equal prominence; "this is not me" burns the
// token and the hotel can see it reached the wrong hands. The web carries
// the entry while the PWA (Sprint 4) takes over the full six-step flow;
// phone verification is the SMS sign-in itself.

interface Invitation {
  status: 'invited' | 'declined' | 'joined' | 'connected';
  businessName: string;
  hotelName: string;
  invitedBy: string;
  history?: { bookingsSettled: number; typicalAmount: Money };
}

type View = 'loading' | 'invalid' | 'invite' | 'phone' | 'done' | 'declined' | 'burned';

export function Join() {
  const [view, setView] = useState<View>('loading');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const token = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('t') ?? '';
  // The one data mechanism works pre-auth: without a session there is no
  // Authorization header, and the invitation endpoints are open by design.
  const loaded = useLoad<Invitation>(token ? `/invitations/${encodeURIComponent(token)}` : null);
  const invitation = loaded.data;

  useEffect(() => {
    if (!token || loaded.error) {
      setView('invalid');
      return;
    }
    if (invitation && view === 'loading') {
      // Resuming mid-flow: an accepted invitation goes straight to the
      // sign-in hand-off; a declined one can still change its mind.
      setView(invitation.status === 'joined' ? 'done' : 'invite');
    }
  }, [token, invitation, loaded.error, view]);

  const act = async (verb: 'accept' | 'decline' | 'burn', body?: Record<string, unknown>) => {
    setBusy(true);
    setError('');
    try {
      await api('POST', `/invitations/${encodeURIComponent(token)}/${verb}`, body);
      setView(verb === 'accept' ? 'done' : verb === 'decline' ? 'declined' : 'burned');
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin-wrap">
      <div className="signin-form-side" style={{ margin: 'auto' }}>
        <div className="signin">
          {view === 'loading' && (
            <>
              <h1>One moment…</h1>
              <p className="welcome">Opening your invitation.</p>
            </>
          )}

          {view === 'invalid' && (
            <>
              <h1>This link isn&rsquo;t valid any more</h1>
              <p className="welcome">
                Invitation links work for 14 days. Ask the hotel to send a fresh one — it takes them
                one click.
              </p>
            </>
          )}

          {view === 'invite' && invitation && (
            <>
              <div className="hero" style={{ marginBottom: 16 }}>
                {/* The person who invited, by name — canon 11's hero list. */}
                <div className="eyebrow">{invitation.invitedBy} at {invitation.hotelName} invited you</div>
                <div className="join-name">{invitation.businessName}</div>
                <div className="join-blurb">
                  {invitation.history
                    ? `${invitation.hotelName} settled ${invitation.history.bookingsSettled} ${
                        invitation.history.bookingsSettled === 1 ? 'booking' : 'bookings'
                      } for its guests recently, typically paying a supplier ${fmtMoney(
                        invitation.history.typicalAmount,
                      )} a time. They want to send that work to you.`
                    : `${invitation.hotelName} sends its guests’ trips and transfers to the suppliers it trusts — and it wants you among them.`}
                </div>
              </div>
              <p className="welcome" style={{ marginTop: 12 }}>
                It starts with your phone — that&rsquo;s your sign-in. Your trips and prices get set up
                with you; no documents, no bank details today.
              </p>
              <button className="btn lg" style={{ width: '100%', marginTop: 8 }} disabled={busy} onClick={() => setView('phone')}>
                Yes, set me up
              </button>
              <button className="btn secondary lg" style={{ width: '100%', marginTop: 8 }} disabled={busy} onClick={() => act('decline')}>
                Not now
              </button>
              <button className="linklike" style={{ marginTop: 16 }} disabled={busy} onClick={() => act('burn')}>
                This is not me
              </button>
              {error && <p className="error-line" role="alert">{error}</p>}
            </>
          )}

          {view === 'phone' && invitation && (
            <>
              <h1>Your mobile number</h1>
              <p className="welcome">
                The phone is your sign-in — no password exists anywhere in this product. A code
                arrives by SMS whenever you open ISO.
              </p>
              <div className="field">
                <label htmlFor="jphone">Mobile phone</label>
                <input
                  id="jphone"
                  className="mono"
                  placeholder="+30 691 234 5678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoFocus
                />
              </div>
              {error && <p className="error-line" role="alert">{error}</p>}
              <button
                className="btn lg"
                style={{ width: '100%', marginTop: 8 }}
                disabled={busy || !/^\+\d{8,15}$/.test(phone.replace(/\s/g, ''))}
                onClick={() => act('accept', { phone: phone.replace(/\s/g, '') })}
              >
                {busy ? 'Setting up…' : 'Continue'}
              </button>
              <button className="linklike" style={{ marginTop: 12 }} disabled={busy} onClick={() => setView('invite')}>
                Back
              </button>
            </>
          )}

          {view === 'done' && (
            <>
              <h1>You&rsquo;re set</h1>
              <p className="welcome">
                Sign in with your phone to see your first screen. {invitation?.hotelName ?? 'The hotel'} will
                be able to book you once your trips are set up.
              </p>
              <a
                href="#/signin"
                className="btn lg"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%', textDecoration: 'none' }}
              >
                Sign in with your phone
              </a>
            </>
          )}

          {view === 'declined' && (
            <>
              <h1>No problem</h1>
              <p className="welcome">
                {invitation?.hotelName ?? 'The hotel'} will see you passed. If you change your mind
                inside 14 days, this same link still works.
              </p>
            </>
          )}

          {view === 'burned' && (
            <>
              <h1>Thanks — that link is now dead</h1>
              <p className="welcome">
                {invitation?.hotelName ?? 'The hotel'} can see the invitation reached the wrong
                person. Nothing else happens.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
